import { type QueryResult } from '@apollo/client';
import { merge } from '@shared/utils';
import {
  useGetUserAppRolesQuery,
  useGetUserMetadataQuery,
  useGetUserProfileQuery,
  useGetUserStatusQuery,
} from '@user-management/graphql';
import { useMemo } from 'react';

import { type User } from '../../types';

import { mapper } from './mapper';

export default function useGetUser(
  userId: string,
): Pick<QueryResult<User>, 'data' | 'loading' | 'error'> {
  const {
    data: userProfile,
    loading: isGettingUserProfile,
    error: errorGettingUserProfile,
  } = useGetUserProfileQuery({
    variables: { userId },
  });
  const {
    data: userStatus,
    loading: isGettingUserStatus,
    error: errorGettingUserStatus,
  } = useGetUserStatusQuery({
    variables: { userId },
  });
  const {
    data: userAppRoles,
    loading: isGettingUserAppRoles,
    error: errorGettingUserAppRoles,
  } = useGetUserAppRolesQuery({
    variables: { userId },
  });
  const {
    data: userMetadata,
    loading: isGettingUserMetadata,
    error: errorGettingUserMetadata,
  } = useGetUserMetadataQuery({
    variables: { userId },
  });

  const user = useMemo(() => {
    if (
      !userProfile?.suUser ||
      !userStatus?.UsBoUser ||
      !userAppRoles?.UsBoUser ||
      !userMetadata?.UsBoUser
    ) {
      return undefined;
    }

    return mapper(
      merge(
        userProfile?.suUser,
        userStatus?.UsBoUser,
        userAppRoles?.UsBoUser,
        userMetadata?.UsBoUser,
      ) as Parameters<typeof mapper>[0],
    );
  }, [userProfile, userStatus, userAppRoles, userMetadata]);

  const isLoading =
    isGettingUserProfile || isGettingUserStatus || isGettingUserAppRoles || isGettingUserMetadata;

  const error =
    errorGettingUserProfile ||
    errorGettingUserStatus ||
    errorGettingUserAppRoles ||
    errorGettingUserMetadata;

  return { data: user, loading: isLoading, error };
}
